/**
 * Beat product page behaviour: <mrk-license-picker> (tier selection + add to cart through Horizon's cart flow),
 * the share button, the description "Read more" toggle and the make-an-offer anchor.
 *
 * Add to cart mirrors Horizon's product-form.js so the cart drawer and cart bubble update natively:
 *   1. dispatch @shopify/events CartLinesUpdateEvent (action 'add', with a deferred promise) from the picker element;
 *   2. POST the form to Theme.routes.cart_add_url with `sections` = every cart-items-component's section ID;
 *   3. resolve the promise with { cart, detail: { sections, items, didError } }.
 * cart-items-component morphs the returned section HTML, cart-icon updates the count, and cart-drawer auto-opens
 * (deferred until the license dialog closes when the picker lives inside a modal <dialog>).
 */
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';

/** Fired on the picker (bubbles) after a successful add. detail: { variantId } */
export const MRK_LICENSE_ADDED = 'mrk:license:added';

async function fetchCart() {
  const cartItems = /** @type {any} */ (document.querySelector('cart-items-component'));
  if (cartItems) {
    await customElements.whenDefined('cart-items-component');
    if (typeof cartItems.fetchCartData === 'function') return cartItems.fetchCartData();
  }
  const response = await fetch(`${Theme.routes.cart_url}.json`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!response.ok) throw new Error(`Cart fetch failed: ${response.status}`);
  return response.json();
}

/**
 * Adds one variant to the cart the way Horizon's product form does.
 * @param {HTMLElement} source - Element the cart events are dispatched from (must be in the document).
 * @param {FormData} formData - Contains `id` and `quantity`.
 * @param {string} productId
 * @returns {Promise<void>}
 */
export async function addToCart(source, formData, productId) {
  const sectionIds = [...document.querySelectorAll('cart-items-component')]
    .map((element) => /** @type {HTMLElement} */ (element).dataset.sectionId)
    .filter(Boolean);
  if (sectionIds.length) formData.append('sections', sectionIds.join(','));

  const itemCount = Number(formData.get('quantity')) || 1;
  const deferred = CartLinesUpdateEvent.createPromise();

  source.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: /** @type {string} */ (formData.get('id')), quantity: itemCount }],
      promise: deferred.promise,
    })
  );

  const config = fetchConfig('javascript', { body: formData });
  let response;
  try {
    response = await fetch(Theme.routes.cart_add_url, {
      ...config,
      headers: { ...config.headers, Accept: 'text/html' },
    }).then((res) => res.json());
  } catch (error) {
    deferred.reject(error);
    source.dispatchEvent(new CartErrorEvent({ error: 'Network error during add to cart', code: 'SERVICE_UNAVAILABLE' }));
    throw error;
  }

  const didError = Boolean(response.status);
  if (didError) {
    source.dispatchEvent(
      new CartErrorEvent({
        error: response.message || 'Add to cart failed',
        code: 'INVALID',
        detail: { description: response.description, errors: response.errors },
      })
    );
  }

  const cart = await fetchCart().catch((error) => {
    deferred.reject(error);
    throw error;
  });
  deferred.resolve({
    cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
    detail: {
      items: cart.items,
      source: 'mrk-license-picker',
      sourceId: productId,
      itemCount,
      productId,
      sections: didError ? undefined : response.sections,
      didError,
    },
  });

  if (didError) throw new Error(response.description || response.message || 'Add to cart failed');
}

/**
 * Opens Horizon's cart drawer after an add when the theme setting "auto open cart drawer" is off (when it's on,
 * cart-drawer.js opens it itself). Inside a modal dialog it waits for that dialog to close, like Horizon does.
 * @param {HTMLElement} source
 */
function openCartDrawer(source) {
  const component = document.querySelector('cart-drawer-component');
  if (!component || component.hasAttribute('auto-open')) return;
  const drawer = /** @type {any} */ (component.closest('theme-drawer'));
  if (!drawer) return;
  const openDrawer = () => {
    if (!drawer.isOpen) drawer.open();
  };
  const modal = source.closest('dialog');
  if (modal?.open) modal.addEventListener('close', openDrawer, { once: true });
  else openDrawer();
}

class MrkLicensePicker extends HTMLElement {
  /** @type {AbortController | null} */
  #abort = null;
  #busy = false;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    this.form?.addEventListener('change', this.#onChange, { signal });
    this.form?.addEventListener('submit', this.#onSubmit, { signal });
    this.addEventListener('click', this.#onClick, { signal });
    this.#sync();
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }

  get form() {
    return this.querySelector('form');
  }

  /** @returns {HTMLInputElement | null} */
  get selected() {
    return this.querySelector('.mrk-tier__input:checked');
  }

  #onChange = () => this.#sync();

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const toggle = target.closest('[data-mrk-terms-toggle]');
    if (toggle) {
      this.#toggleTerms(/** @type {HTMLButtonElement} */ (toggle));
    }
  };

  /** @param {HTMLButtonElement} toggle */
  #toggleTerms(toggle) {
    const panel = document.getElementById(toggle.getAttribute('aria-controls') || '');
    if (!panel) return;
    const expanded = toggle.getAttribute('aria-expanded') !== 'true';
    toggle.setAttribute('aria-expanded', String(expanded));
    panel.hidden = !expanded;
    const label = toggle.querySelector('[data-mrk-terms-label]');
    if (label) label.textContent = expanded ? this.dataset.mrkLabelHideTerms || '' : this.dataset.mrkLabelViewTerms || '';
  }

  #sync() {
    const input = this.selected;
    const label = this.querySelector('[data-mrk-add-label]');
    if (input && label && !this.#busy) {
      label.textContent = (this.dataset.mrkLabelAdd || '').replace('[price]', input.dataset.mrkPrice || '');
    }
    const offer = /** @type {HTMLElement | null} */ (this.querySelector('[data-mrk-offer-link]'));
    if (offer) offer.hidden = input?.dataset.mrkKind !== 'exclusive';
  }

  /** @param {string} text @param {boolean} [isError] */
  #status(text, isError = false) {
    const status = this.querySelector('[data-mrk-status]');
    if (!status) return;
    status.textContent = text;
    status.classList.toggle('mrk-license-picker__status--error', isError);
  }

  /** @param {SubmitEvent} event */
  #onSubmit = async (event) => {
    event.preventDefault();
    const input = this.selected;
    const form = this.form;
    const button = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-mrk-add]'));
    if (!input || !form || !button || this.#busy || button.disabled) return;

    this.#busy = true;
    button.setAttribute('aria-busy', 'true');
    const label = button.querySelector('[data-mrk-add-label]');
    if (label) label.textContent = this.dataset.mrkLabelAdding || '';
    this.#status('');

    try {
      await addToCart(this, new FormData(form), this.dataset.mrkProductId || '');
      this.#status(this.dataset.mrkLabelAdded || '');
      openCartDrawer(this);
      this.dispatchEvent(new CustomEvent(MRK_LICENSE_ADDED, { bubbles: true, detail: { variantId: input.value } }));
    } catch {
      this.#status(this.dataset.mrkLabelError || '', true);
    } finally {
      this.#busy = false;
      button.removeAttribute('aria-busy');
      this.#sync();
    }
  };
}

if (!customElements.get('mrk-license-picker')) customElements.define('mrk-license-picker', MrkLicensePicker);

/* ---------- Page-only behaviour (idempotent, delegated) ---------- */

function setupDescription() {
  for (const wrapper of document.querySelectorAll('[data-mrk-description]:not([data-mrk-ready])')) {
    wrapper.setAttribute('data-mrk-ready', '');
    const body = wrapper.querySelector('[data-mrk-description-body]');
    const toggle = /** @type {HTMLButtonElement | null} */ (wrapper.querySelector('.mrk-beat-product__read-more'));
    if (!body || !toggle) continue;
    wrapper.classList.add('mrk-beat-product__description--clamped');
    if (body.scrollHeight <= body.clientHeight + 2) {
      wrapper.classList.remove('mrk-beat-product__description--clamped');
      continue;
    }
    toggle.hidden = false;
    toggle.addEventListener('click', () => {
      const expanded = toggle.getAttribute('aria-expanded') !== 'true';
      toggle.setAttribute('aria-expanded', String(expanded));
      wrapper.classList.toggle('mrk-beat-product__description--clamped', !expanded);
      toggle.textContent = expanded ? toggle.dataset.mrkLabelLess || '' : toggle.dataset.mrkLabelMore || '';
    });
  }
}

function openOfferFromHash() {
  if (location.hash !== '#mrk-offer' && !location.hash.startsWith('#mrk-offer-form')) return;
  const offer = /** @type {HTMLDetailsElement | null} */ (document.querySelector('[data-mrk-offer]'));
  if (!offer) return;
  offer.open = true;
  offer.scrollIntoView({ block: 'start', behavior: 'smooth' });
  /** @type {HTMLElement | null} */ (offer.querySelector('[role="status"], [role="alert"], input:not([type="hidden"])'))?.focus({
    preventScroll: true,
  });
}

if (!window.mrkProductPageReady) {
  window.mrkProductPageReady = true;

  document.addEventListener('click', (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const offerLink = /** @type {HTMLAnchorElement | null} */ (target.closest('[data-mrk-offer-link]'));
    if (offerLink && offerLink.pathname === location.pathname && document.querySelector('[data-mrk-offer]')) {
      event.preventDefault();
      history.replaceState(null, '', '#mrk-offer');
      openOfferFromHash();
    }
  });

  window.addEventListener('hashchange', openOfferFromHash);
  setupDescription();
  openOfferFromHash();
}
